import { useState } from "react";

/**
 * Iver finish + backplate options.
 *
 * Rendered collapsed by default — a small summary chip ("8 Finishes · 4
 * Backplates ⌄") keeps the product grid tidy; clicking expands the full list
 * with finish dots + names and backplate pills so cards don't dominate the
 * visual real estate.
 */
export const IVER_FINISH_HEX = {
  "Brushed Brass": "#b0904f",
  "Signature Brass": "#8f6f3b",
  "Polished Brass": "#d6b062",
  "Matt Black": "#1c1c1c",
  "Brushed Chrome": "#bcbcba",
  "Polished Chrome": "#d8d8d6",
  "Satin Nickel": "#a19b93",
  "Brushed Gold PVD": "#c9a05e",
};

export default function IverOptions({ finishes = [], backplates = [], code }) {
  const [open, setOpen] = useState(false);
  if (!finishes.length && !backplates.length) return null;

  const summaryParts = [];
  if (finishes.length) summaryParts.push(`${finishes.length} Finishes`);
  if (backplates.length) summaryParts.push(`${backplates.length} Backplates`);

  return (
    <div
      data-testid={`iver-options-${code.toLowerCase()}`}
      className="mt-3 flex w-full flex-col items-center gap-2 px-1"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        data-testid={`iver-options-toggle-${code.toLowerCase()}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#0a0a0c]/15 bg-white px-3 py-1 font-tech text-[9px] font-semibold uppercase tracking-[0.24em] text-[#0a0a0c]/75 transition-colors duration-300 hover:border-[#c6a87c] hover:text-[#c6a87c]"
      >
        <span>{summaryParts.join(" · ")}</span>
        <span
          aria-hidden
          className={`text-[8px] transition-transform duration-300 ${
            open ? "rotate-180" : "rotate-0"
          }`}
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          data-testid={`iver-options-panel-${code.toLowerCase()}`}
          className="flex w-full flex-col items-center gap-3"
        >
          {finishes.length > 0 && (
            <div className="flex w-full flex-col items-center gap-1.5">
              <p className="font-tech text-[9px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/55">
                Finishes
              </p>
              <ul className="flex w-full flex-wrap justify-center gap-x-2 gap-y-1.5">
                {finishes.map((f) => (
                  <li
                    key={f}
                    data-testid={`iver-finish-${code.toLowerCase()}-${f
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0a0a0c]/[0.03] px-1.5 py-0.5"
                  >
                    <span
                      aria-hidden
                      className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-inset ring-[#0a0a0c]/25"
                      style={{ backgroundColor: IVER_FINISH_HEX[f] || "#999" }}
                    />
                    <span className="font-tech text-[9px] font-semibold uppercase tracking-[0.14em] text-[#0a0a0c]/75">
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {backplates.length > 0 && (
            <div className="flex flex-col items-center gap-1.5">
              <p className="font-tech text-[9px] font-bold uppercase tracking-[0.3em] text-[#0a0a0c]/55">
                Backplate
              </p>
              <div className="flex flex-wrap justify-center gap-1">
                {backplates.map((b) => (
                  <span
                    key={b}
                    data-testid={`iver-backplate-${code.toLowerCase()}-${b
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    className="inline-block rounded-full border border-[#0a0a0c]/20 px-2 py-0.5 font-tech text-[9px] font-semibold uppercase tracking-[0.14em] text-[#0a0a0c]/75"
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
